import classNames from "classnames";
import { memo } from "react";
import type {
  ContentBlock,
  SidebarBlock,
  StepSkeletonConfig,
} from "../../step-skeletons";
import * as layoutStyles from "../layouts/two-column-layout.module.css";
import * as styles from "./step-skeleton.module.css";

function Bone({ className }: { className?: string }) {
  return <div className={classNames(styles.bone, className)} />;
}

function BlockRenderer({ block }: { block: ContentBlock }) {
  switch (block) {
    case "toggle-row":
      return (
        <div className={styles.toggleRowBone}>
          <Bone className={styles.toggleIconBone} />
          <Bone className={styles.toggleLabelBone} />
          <Bone className={styles.toggleSwitchBone} />
        </div>
      );
    case "video":
      return (
        <div className={styles.videoBone}>
          <Bone className={styles.videoAreaBone} />
          <div className={styles.videoFooterBone}>
            <Bone className={styles.videoFooterIconBone} />
            <Bone className={styles.videoFooterTextBone} />
          </div>
        </div>
      );
    case "image-grid":
      return (
        <div className={styles.imageGridBone}>
          <Bone className={styles.imageGridCellBone} />
          <Bone className={styles.imageGridCellBone} />
          <Bone className={styles.imageGridCellBone} />
          <Bone className={styles.imageGridCellBone} />
        </div>
      );
    case "phone":
      return (
        <div className={styles.phoneWrap}>
          <Bone className={styles.phoneBone} />
        </div>
      );
    case "hero-image":
      return <Bone className={styles.heroImageBone} />;
    case "card":
      return (
        <div className={styles.cardBone}>
          <Bone className={styles.cardTitleBone} />
          <Bone className={styles.cardLineBone} />
        </div>
      );
    case "pillbox":
      return <Bone className={styles.pillboxBone} />;
  }
}

function SidebarBlockRenderer({ block }: { block: SidebarBlock }) {
  switch (block) {
    case "timeline-item":
      return (
        <div className={styles.sidebarTimelineItem}>
          <Bone className={styles.timelineIconBone} />
          <Bone className={styles.timelineTextBone} />
        </div>
      );
  }
}

export const StepSkeleton = memo(function StepSkeleton({
  config,
}: {
  config?: StepSkeletonConfig;
}) {
  const hideBack = config?.hideBack ?? false;

  return (
    <div className={layoutStyles.layout}>
      <div className={layoutStyles.leftColumn}>
        <div className={classNames(layoutStyles.leftInner, styles.leftInner)}>
          <div className={styles.spacer} />
          {!hideBack && <Bone className={styles.backBone} />}
          <Bone className={styles.titleBone} />
          <Bone className={styles.subtitleBone} />
          {config?.sidebar && config.sidebar.length > 0 && (
            <div className={styles.sidebar}>
              {config.sidebar.map((block, i) => (
                <SidebarBlockRenderer block={block} key={i} />
              ))}
            </div>
          )}
          <Bone className={styles.buttonBone} />
        </div>
      </div>

      <div className={layoutStyles.rightColumn}>
        <div className={layoutStyles.rightInner}>
          <div className={styles.blocks}>
            {(config?.blocks ?? []).map((block, i) => (
              <BlockRenderer block={block} key={i} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
});
